import { useEffect, useState } from "react";
import { XCircle, Loader2, Calculator, Repeat, RefreshCw, Landmark, Sparkles, CreditCard } from "lucide-react";
import { toast } from "sonner";
import api, { formatPriceExact as formatPrice } from "../../../lib/api";
import { Button } from "../../ui/button";
import { Input } from "../../ui/input";
import { Label } from "../../ui/label";
import { Textarea } from "../../ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "../../ui/dialog";
import { useAutosaveOnBlur, AutosaveBadge } from "./useAutosaveOnBlur";
import { DispoBackButton } from "./DispoBackButton";
import { FeatureGuideTriangle } from "./FeatureGuideTriangle";
import { InfoHint } from "../InfoHint";
import { collectMissing, focusFirstMissing } from "../../../lib/formValidation";

// ---------- Phase 4 — Zahlungskonditionen, Rabatt & Mischzahlung --------------
// Feb-2026 (User-Wunsch): „EC-Karte" als erste Option + Vorauswahl bei neuen
// Zahlungszeilen (statt Barzahlung).
const PAY_METHODS = [
    { value: "ec_karte", label: "EC-Karte" },
    { value: "bar", label: "Barzahlung (bei Fahrzeugverkäufen nicht möglich)", disabled: true },
    { value: "ueberweisung", label: "Überweisung" },
    // Jul-2026 (User-Wunsch): „Finanzierung / Leasing" in ZWEI Zahlarten getrennt.
    { value: "finanzierung", label: "Finanzierung" },
    { value: "leasing", label: "Leasing" },
    { value: "inzahlungnahme", label: "Inzahlungnahme", needsTradein: true },
];
// „Echte" Zahlungswege (ohne Inzahlungnahme/Anzahlung) — daraus wird die
// Zahlungsbedingung automatisch abgeleitet (dominanter Zahlungsweg).
const REAL_PAY_METHODS = ["ec_karte", "bar", "ueberweisung", "finanzierung", "leasing"];

// Jun-2026 (User-Wunsch) — Standard-Hinweistext für Bestellfahrzeuge
// (Lager-Status „Bestellung"). Wird unter „Notizen zu den Konditionen"
// vorbelegt, wenn noch keine Notiz vorhanden ist.
const ORDER_DELIVERY_NOTE =
    "Hinweis zu Bestellfahrzeugen: Die genannten Liefertermine sind unverbindlich. " +
    "Erfahrungsgemäß können die Lieferzeiten herstellerseitig stark schwanken und in der " +
    "Regel zwischen 4 und 12 Wochen liegen. Wir halten Sie selbstverständlich laufend über " +
    "den aktuellen Stand Ihrer Bestellung auf dem Laufenden.";

export function PaymentConditionsModal({ listingId, base, tradein, onBack, onClose, onSaved, onOpenTradein, onContinue }) {
    const [data, setData] = useState(base || { vehicle_price: 0, items_total: 0, subtotal: 0 });
    // Jun-2026 (User-Wunsch) — Endpreis mit berechneter Zwischensumme vorbelegen
    // (änderbar). Ein echter ausgehandelter Rabatt (Endpreis < Zwischensumme)
    // bleibt erhalten; ein veralteter höherer Endpreis wird auf die Zwischensumme
    // zurückgesetzt.
    const initFinal = (sub, fin) => (Number(fin) > 0 && Number(fin) < Number(sub)) ? Number(fin) : Number(sub || 0);
    const [finalPrice, setFinalPrice] = useState(initFinal(base?.subtotal, base?.final_price));
    const [payments, setPayments] = useState((base?.payments || []).map((p) => ({ ...p })));
    const [notes, setNotes] = useState(base?.notes || "");
    const [aiNote, setAiNote] = useState(false);
    const [touched, setTouched] = useState(false);
    const [saving, setSaving] = useState(false);
    // Jun-2026 (User-Wunsch · Msg 509) — Reservierung wurde in den Kaufvertrag-
    // Schritt (ContractSignModal) verschoben.
    // Jun-2026 (User-Wunsch) — Finanzierungs-Detailerfassung im Mischzahlungs-Flow
    // (Bank + Antragsnr./Laufzeit/Rate/Anzahlung/Schlussrate). Speichert in
    // dispo_financing und wird im Bankgelder-Tab weitergeführt.
    const [banks, setBanks] = useState([]);
    // Aug-2026 (User-Wunsch) — Favorisierte Finanzierungspartner (Zahnrad im
    // Upload-Belege): sind Favoriten hinterlegt, werden NUR diese angezeigt,
    // gefiltert nach dem Fahrzeug-Hersteller.
    const [finFavs, setFinFavs] = useState({ finanzierung: [], leasing: [] });
    const [vehBrand, setVehBrand] = useState("");
    // Jun-2026 (User-Wunsch) — Standard-Vorschläge, wenn KEINE Banken/Leasinggeber
    // als Dienstleister hinterlegt sind. Auswahl füllt den Bankname (Freitext).
    const DEFAULT_BANK_NAMES = ["Santander Consumer Bank", "BMW Bank", "TARGOBANK", "CreditPlus Bank", "Bank11", "Honda Bank", "Yamaha Motor Finance", "Younited Credit", "Sparkasse", "Volksbank Raiffeisenbank"];
    // Jul-2026 (User-Wunsch) — eigene Vorschlagsliste für Leasinggesellschaften.
    const DEFAULT_LEASING_NAMES = ["ALD Automotive / Ayvens", "Arval", "Deutsche Leasing", "Santander Leasing", "BMW Leasing", "Leaseplan", "Sixt Leasing", "Mobility Concept"];
    const [bankCustom, setBankCustom] = useState(false);
    const [fin, setFin] = useState({ bank_id: "", bank_name: "", finance_number: "", term_months: "", monthly_rate: "", down_payment: "", final_rate: "", status: "docs_open", financing_approved: false });
    const [extraEarnings, setExtraEarnings] = useState("");
    // Jul-2026 (User-Wunsch) — Gutschein-Einlösung direkt im Kaufvertrag
    const [voucher, setVoucher] = useState(null);
    const [voucherCode, setVoucherCode] = useState("");
    const [voucherBusy, setVoucherBusy] = useState(false);
    const redeemVoucher = async () => {
        const code = voucherCode.trim();
        if (!code) { toast.error("Bitte Gutschein-Code eingeben"); return; }
        setVoucherBusy(true);
        try {
            const r = await api.post(`/listings/${listingId}/dispo/voucher`, { code });
            setVoucher(r.data?.dispo_payment?.voucher || null);
            setVoucherCode("");
            toast.success("Gutschein eingelöst — wird automatisch verrechnet.");
        } catch (e) {
            toast.error(e.response?.data?.detail || "Gutschein konnte nicht eingelöst werden");
        } finally { setVoucherBusy(false); }
    };
    const removeVoucher = async () => {
        setVoucherBusy(true);
        try {
            await api.delete(`/listings/${listingId}/dispo/voucher`);
            setVoucher(null);
            toast.success("Gutschein entfernt.");
        } catch (e) {
            toast.error(e.response?.data?.detail || "Gutschein konnte nicht entfernt werden");
        } finally { setVoucherBusy(false); }
    };
    const setFinField = (k, v) => { setTouched(true); setFin((p) => ({ ...p, [k]: v })); };
    const onBankChange = (id) => { const b = banks.find((x) => x.id === id); setTouched(true); setFin((p) => ({ ...p, bank_id: id, bank_name: b ? b.name : p.bank_name })); };

    // Eigener Fetch beim Öffnen — zuverlässiges Timing (User-Klick), unabhängig
    // vom Parent-Lade-Race. Prop `base` dient als Sofort-Anzeige.
    useEffect(() => {
        let alive = true;
        api.get(`/listings/${listingId}/dispo/payment`).then((r) => {
            if (!alive) return;
            const d = r.data || {};
            setData(d);
            setVoucher(d.voucher || null);
            if (!touched) {
                setFinalPrice(initFinal(d.subtotal, d.final_price));
                setExtraEarnings(d.extra_earnings ? String(d.extra_earnings) : "");
                let pays = (d.payments || []).map((p) => ({ ...p }));
                // Jun-2026 (User-Wunsch) — Inzahlungnahme-Zahlart automatisch aus dem
                // erfassten Eintausch befüllen (Betrag = Ankaufspreis), damit der
                // Betrag nicht doppelt gepflegt werden muss.
                const tp = Math.round(Number(tradein?.price || 0) * 100) / 100;
                if (tp > 0) {
                    const i = pays.findIndex((p) => p.method === "inzahlungnahme");
                    if (i >= 0) pays[i] = { ...pays[i], amount: tp };
                    else pays = [...pays, { method: "inzahlungnahme", amount: tp }];
                }
                // Jun-2026 (User-Wunsch) — automatischer Ausgleich beim Öffnen, wenn
                // genau EIN echter (nicht-Inzahlungnahme) Zahlweg vorhanden ist, damit
                // selbst der eine Klick auf „Restbetrag ausgleichen" entfällt.
                const fpOpen = Number(initFinal(d.subtotal, d.final_price)) || 0;
                const inzAmt = pays.filter((p) => p.method === "inzahlungnahme").reduce((s, p) => s + (Number(p.amount) || 0), 0);
                const netOpen = Math.round((fpOpen - Math.max(tp, inzAmt)) * 100) / 100;
                const realIdxs = pays.map((_, i) => i).filter((i) => pays[i].method !== "inzahlungnahme");
                if (realIdxs.length === 1) {
                    const ti = realIdxs[0];
                    pays[ti] = { ...pays[ti], amount: Math.max(netOpen, 0) };
                }
                setPayments(pays);
                // Jun-2026 (User-Wunsch) — bei Bestellfahrzeugen (Lager-Status
                // „Bestellung") einen freundlichen Liefertermin-Hinweis vorbelegen,
                // sofern noch keine Notiz gespeichert ist.
                const savedNotes = d.notes || "";
                if (!savedNotes && d.stock_state === "bestell") {
                    setNotes(ORDER_DELIVERY_NOTE);
                } else {
                    setNotes(savedNotes);
                }
            }
        }).catch(() => {});
        return () => { alive = false; };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [listingId]);

    useEffect(() => {
        let alive = true;
        Promise.all([
            api.get("/dealer/banks").catch(() => ({ data: { banks: [] } })),
            api.get(`/listings/${listingId}/dispo/financing`).catch(() => ({ data: {} })),
            api.get(`/dealer/finance-partner-settings?listing_id=${listingId}`).catch(() => ({ data: {} })),
        ]).then(([b, f, fp]) => {
            if (!alive) return;
            setBanks(b.data?.banks || []);
            setFinFavs(fp.data?.favorites || { finanzierung: [], leasing: [] });
            setVehBrand(fp.data?.listing_brand || "");
            const d = f.data || {};
            // „Andere…"-Modus aktiv, wenn ein Freitext-Bankname existiert, der nicht
            // in den Standard-Vorschlägen ist (nur relevant ohne Dienstleister-Banken).
            if (d.bank_name && !DEFAULT_BANK_NAMES.includes(d.bank_name)) setBankCustom(true);
            setFin({
                bank_id: d.bank_id || "", bank_name: d.bank_name || "",
                finance_number: d.finance_number || "",
                term_months: d.term_months ?? "", monthly_rate: d.monthly_rate ?? "",
                down_payment: d.down_payment ?? "", final_rate: d.final_rate ?? "",
                status: d.status || "docs_open",
                financing_approved: Boolean(d.financing_approved),
            });
        });
        return () => { alive = false; };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [listingId]);

    const subtotal = Number(data?.subtotal || 0);
    const finalNum = Number(finalPrice) || 0;
    const discountAmount = Math.round((subtotal - finalNum) * 100) / 100;
    const discountPct = subtotal > 0 ? Math.round((discountAmount / subtotal) * 10000) / 100 : 0;
    // Jul-2026 (User-Wunsch · 341) — Rabatt-Limit für dieses Fahrzeug laden
    // (Standard 5 %, Regeln je Marke/Modell/Betrag/Standzeit in den Einstellungen).
    const [discountLimit, setDiscountLimit] = useState(null);
    useEffect(() => {
        api.get(`/listings/${listingId}/dispo/discount-limit`)
            .then((r) => setDiscountLimit(r.data))
            .catch(() => setDiscountLimit(null));
    }, [listingId]);
    const limitExceeded = Boolean(discountLimit?.enabled) && discountAmount > 0
        && discountPct > (Number(discountLimit.max_pct) || 0) + 0.001;
    // Jul-2026 (User-Wunsch) — EU-steuerfreier Verkauf (innergemeinschaftliche
    // Lieferung): Backend rechnet netto, UI zeigt Hinweis, keine Finanzierung.
    const euTaxFree = Boolean(data?.eu_tax_free);
    // Jul-2026 (User-Wunsch) — Rabatt-Freigabe: genehmigte Anfrage erlaubt Überschreitung.
    const discountApproval = discountLimit?.approval;
    const approvalOk = discountApproval?.status === "approved"
        && discountPct <= (Number(discountApproval.requested_pct) || 0) + 0.011;
    const [reqApprovalBusy, setReqApprovalBusy] = useState(false);
    const requestDiscountApproval = async () => {
        setReqApprovalBusy(true);
        try {
            const r = await api.post(`/listings/${listingId}/dispo/discount-approval/request`, { final_price: finalNum });
            setDiscountLimit((p) => ({ ...p, approval: r.data?.approval }));
            toast.success("Freigabe angefordert — der Filialleiter wurde informiert.");
        } catch (e) {
            toast.error(e.response?.data?.detail || "Freigabe-Anfrage fehlgeschlagen");
        } finally { setReqApprovalBusy(false); }
    };
    // Jun-2026 (User-Wunsch) — Inzahlungnahme: verhandelter Eintausch-Preis aus dem
    // Trade-In-Datensatz ODER der Inzahlungnahme-Zahlart; mindert den Endpreis VOR
    // den Zahlungsarten. Restbetrag = Endpreis − Inzahlungnahme.
    const inzahlungPay = payments.filter((p) => p.method === "inzahlungnahme").reduce((s, p) => s + (Number(p.amount) || 0), 0);
    const tradeInAmount = Math.round(Math.max(Number(tradein?.price || 0), inzahlungPay) * 100) / 100;
    const voucherDiscount = Math.round(Number(voucher?.discount_gross || 0) * 100) / 100;
    const netToPay = Math.round((finalNum - tradeInAmount - voucherDiscount) * 100) / 100;
    // Zahlungsarten für den Restbetrag = alle OHNE Inzahlungnahme.
    const paidTotal = payments.filter((p) => p.method !== "inzahlungnahme").reduce((s, p) => s + (Number(p.amount) || 0), 0);
    const balance = Math.round((netToPay - paidTotal) * 100) / 100;
    // Jun-2026 (User-Wunsch) — „Zahlungsbedingung" und „Zahlungsarten" wurden
    // zusammengeführt: die Zahlungsbedingung wird automatisch aus dem dominanten
    // (betragsgrößten) echten Zahlungsweg abgeleitet — kein separates Feld mehr.
    const derivedTermsCode = (() => {
        const real = payments.filter((p) => REAL_PAY_METHODS.includes(p.method) && Number(p.amount) > 0);
        if (!real.length) return "";
        return real.slice().sort((a, b) => (Number(b.amount) || 0) - (Number(a.amount) || 0))[0].method;
    })();
    // Inzahlungnahme-Hinweis nur, solange noch KEIN Eintausch-Fahrzeug erfasst ist.
    const showTradeinHint = !tradein && payments.some((p) => p.method === "inzahlungnahme" && Number(p.amount) > 0);
    // Finanzierungsblock anzeigen, sobald „Finanzierung" als Zahlungsart gewählt ist.
    const hasFinancing = payments.some((p) => p.method === "finanzierung" || p.method === "leasing");
    // Jul-2026 (User-Wunsch): bei Leasing zeigen wir nur Leasinggesellschaften,
    // bei Finanzierung nur Banken (Partner-Checkboxen ``finance_types``).
    const finKind = payments.some((p) => p.method === "leasing") && !payments.some((p) => p.method === "finanzierung") ? "leasing" : "finanzierung";
    // Aug-2026 (User-Wunsch): Sind Favoriten hinterlegt, NUR diese anzeigen —
    // zusätzlich nach Fahrzeug-Hersteller gefiltert (leer = alle Hersteller).
    const favList = finFavs[finKind] || [];
    const brandLc = (vehBrand || "").trim().toLowerCase();
    const banksFor = favList.length
        ? favList
            .filter((f) => !(f.brands || []).length || (f.brands || []).some((x) => String(x).trim().toLowerCase() === brandLc))
            .map((f) => banks.find((b) => b.id === f.bank_id) || { id: f.bank_id, name: f.name })
        : banks.filter((b) => !(b.finance_types || []).length || (b.finance_types || []).includes(finKind));

    // Jun-2026 (User-Wunsch) — neue Zahlart automatisch mit dem offenen Restbetrag
    // vorbelegen, damit keine „Überzahlung" entsteht und die Zahlung mit einem
    // Klick stimmig ist.
    const openRemaining = Math.max(Math.round((netToPay - paidTotal) * 100) / 100, 0);
    const addPay = () => { setTouched(true); setPayments((p) => [...p, { method: "ec_karte", amount: openRemaining }]); };
    const updPay = (idx, k, v) => { setTouched(true); setPayments((p) => p.map((r, i) => i === idx ? { ...r, [k]: v } : r)); };
    const rmPay = (idx) => { setTouched(true); setPayments((p) => p.filter((_, i) => i !== idx)); };
    // Ein-Klick: letzten echten (nicht-Inzahlungnahme) Zahlweg so anpassen, dass
    // die Summe genau dem Restbetrag entspricht (Ausgleich). Existiert kein echter
    // Zahlweg, wird eine Überweisung über den Restbetrag angelegt.
    const balancePayments = () => {
        setTouched(true);
        setPayments((prev) => {
            const realIdxs = prev.map((p, i) => i).filter((i) => prev[i].method !== "inzahlungnahme");
            if (realIdxs.length === 0) {
                return [...prev, { method: "ueberweisung", amount: Math.max(Math.round(netToPay * 100) / 100, 0) }];
            }
            const target = realIdxs[realIdxs.length - 1];
            const othersReal = prev.reduce((s, p, i) => (p.method !== "inzahlungnahme" && i !== target) ? s + (Number(p.amount) || 0) : s, 0);
            const needed = Math.max(Math.round((netToPay - othersReal) * 100) / 100, 0);
            return prev.map((p, i) => i === target ? { ...p, amount: needed } : p);
        });
    };

    // Jul-2026 (User-Wunsch) — bei EC-Karten-Zahlart: Betrag direkt ans gekoppelte
    // SumUp-Terminal senden (kein manuelles Eintippen am Gerät). Erfolgreiche
    // Zahlung wird vom Backend automatisch als Zahlungseingang verbucht.
    const [sendingTerminal, setSendingTerminal] = useState(null);
    // Jul-2026 (User-Wunsch): Terminal-Button überall nur zeigen, wenn unter
    // Buchhaltung ein SumUp-Terminal per Pairing-Code gekoppelt wurde.
    const [terminalReady, setTerminalReady] = useState(false);
    useEffect(() => {
        api.get("/buchhaltung/ec/sumup/readers")
            .then((r) => setTerminalReady((r.data?.items || []).length > 0))
            .catch(() => setTerminalReady(false));
    }, []);
    const sendToTerminal = async (idx) => {
        const p = payments[idx];
        const val = Number(p.amount) || 0;
        if (val <= 0) { toast.error("Bitte zuerst einen Betrag eingeben"); return; }
        setSendingTerminal(idx);
        try {
            await api.post("/buchhaltung/ec/sumup/terminal-checkout", {
                amount: val,
                description: "Fahrzeugkauf EC-Zahlung",
                listing_id: listingId,
            });
            toast.success(`${formatPrice(val)} an das SumUp-Terminal gesendet — Karte am Gerät vorhalten`);
        } catch (err) {
            toast.error(err?.response?.data?.detail || "Zahlung konnte nicht ans Terminal gesendet werden");
        } finally { setSendingTerminal(null); }
    };

    const save = async ({ silent = false } = {}) => {
        // Jul-2026 (User-Wunsch) — Finanzierungs-Pflichtfelder validieren (kein
        // leeres/0-Feld beim expliziten Speichern). Silent-Autosave überspringt.
        if (!silent && hasFinancing) {
            const missing = collectMissing([
                { ok: !!(fin.bank_name || fin.bank_id), label: "Bank / Leasinggeber", testid: banks.length > 0 ? "dispo-payment-fin-bank" : "dispo-payment-fin-bank-suggest" },
                { ok: !!String(fin.finance_number || "").trim(), label: "Antragsnummer", testid: "dispo-payment-fin-number" },
                { ok: Number(fin.term_months) > 0, label: "Laufzeit (Monate)", testid: "dispo-payment-fin-term" },
                { ok: Number(fin.monthly_rate) > 0, label: "Mtl. Rate (€)", testid: "dispo-payment-fin-monthly" },
            ]);
            if (missing.length) { focusFirstMissing(missing); return false; }
        }
        // Jul-2026 (User-Wunsch) — mindestens EINE Zahlungsart ist Pflicht.
        if (!silent && !payments.some((p) => REAL_PAY_METHODS.includes(p.method) && Number(p.amount) > 0)) {
            toast.error("Bitte mindestens eine Zahlungsart mit Betrag erfassen (EC-Karte, Überweisung oder Finanzierung).");
            return false;
        }
        // Jul-2026 (User-Wunsch · 341) — Rabatt-Limit hart durchsetzen (Mitarbeiter
        // ohne Leitungsfunktion); Backend prüft zusätzlich serverseitig.
        if (!silent && limitExceeded && discountLimit?.enforced && !approvalOk) {
            toast.error(`Rabatt-Limit überschritten: max. ${discountLimit.max_pct} % erlaubt — gewünscht ${discountPct} %. Bitte Freigabe beim Filialleiter anfordern.`);
            return false;
        }
        if (!silent) setSaving(true);
        try {
            await api.patch(`/listings/${listingId}/dispo/payment`, {
                final_price: Number(finalPrice) || 0,
                payments: payments.map((p) => ({ method: p.method, amount: Number(p.amount) || 0 })),
                notes,
                payment_terms_code: derivedTermsCode,
                extra_earnings: Number(extraEarnings) || 0,
            });
            // Jun-2026 (User-Wunsch) — Finanzierungsdaten mitspeichern (Status erhalten).
            if (hasFinancing) {
                await api.patch(`/listings/${listingId}/dispo/financing`, {
                    bank_id: fin.bank_id || null,
                    bank_name: fin.bank_name || "",
                    finance_number: fin.finance_number || "",
                    finance_kind: finKind,
                    term_months: fin.term_months === "" ? null : Number(fin.term_months),
                    monthly_rate: fin.monthly_rate === "" ? null : Number(fin.monthly_rate),
                    down_payment: fin.down_payment === "" ? null : Number(fin.down_payment),
                    final_rate: fin.final_rate === "" ? null : Number(fin.final_rate),
                    status: fin.status || "docs_open",
                    financing_approved: Boolean(fin.financing_approved),
                });
            }
            if (!silent) {
                toast.success("Zahlungskonditionen gespeichert");
                autosave.markSaved();
                // Feb-2026 (User-Wunsch) — Hinweis bei verbleibender Zahlungsdifferenz.
                if (balance > 0.01) {
                    toast.warning(`Achtung: Es besteht noch eine offene Zahlungsdifferenz von ${formatPrice(balance)}.`);
                } else if (balance < -0.01) {
                    toast.warning(`Achtung: Überzahlung von ${formatPrice(Math.abs(balance))}.`);
                }
                await onSaved?.();
            }
            return true;
        } catch (e) {
            if (!silent) toast.error(e.response?.data?.detail || "Speichern fehlgeschlagen");
            return false;
        } finally { if (!silent) setSaving(false); }
    };
    const autosave = useAutosaveOnBlur(
        () => save({ silent: true }),
        () => JSON.stringify({ finalPrice, payments, notes, fin }),
    );

    // Jun-2026 (User-Wunsch) — Notiztext mit KI „schönmachen" (Bestellfahrzeuge).
    const isOrderVehicle = data?.stock_state === "bestell";
    const improveNotes = async () => {
        setAiNote(true);
        try {
            const { data: r } = await api.post(`/listings/${listingId}/dispo/payment/notes-draft`, { current: notes });
            if (r?.message) {
                setTouched(true);
                setNotes(r.message);
                toast.success(r.ai ? "Text mit KI verbessert" : "Hinweistext eingefügt");
            }
        } catch {
            toast.error("KI-Text fehlgeschlagen");
        } finally {
            setAiNote(false);
        }
    };

    return (
        <Dialog open onOpenChange={(o) => !o && onClose()}>
            <DialogContent onPointerDownOutside={(e) => e.preventDefault()} onInteractOutside={(e) => e.preventDefault()} className="w-[96vw] max-w-5xl max-h-[92vh] overflow-y-auto" data-testid="dispo-modal-payment" onBlur={autosave.onBlur}>
                <FeatureGuideTriangle guideKey="payment" />
                <DialogHeader><DialogTitle className="flex items-center gap-2"><Calculator className="w-5 h-5 text-orange-600" /> Zahlung &amp; Rabatt<span className="ml-auto text-[10px] font-bold uppercase tracking-wider text-gray-400 mr-6">Schritt 4 von 6</span></DialogTitle><DialogDescription>Endpreis, Rabatt und Zahlungsarten (inkl. Finanzierung) erfassen. Die Reservierung erfolgt im Kaufvertrag-Schritt.</DialogDescription></DialogHeader>
                <AutosaveBadge status={autosave.status} savedAt={autosave.savedAt} />

                {/* Jun-2026 (User-Wunsch) — 2-spaltig & breiter, damit kein vertikales
                    Scrollen mehr nötig ist. Links: Preise & Zahlungsarten · Rechts:
                    Finanzierung, Eintausch-Hinweis & Notizen. */}
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-x-5 gap-y-2 items-start">
                  <div className="space-y-2 min-w-0">
                {/* Jul-2026 (User-Wunsch) — EU-steuerfreier Verkauf: gut sichtbarer Hinweis */}
                {euTaxFree && (
                    <div className="border-2 border-sky-500 bg-sky-50 rounded-sm p-3 text-[12px] text-sky-900 space-y-1.5" data-testid="dispo-payment-eu-banner">
                        <div className="font-black text-sm flex items-center gap-1.5">🇪🇺 Innergemeinschaftliche Lieferung — steuerfreier EU-Verkauf (§4 Nr. 1b i. V. m. §6a UStG)</div>
                        <div><b>Alle Beträge werden NETTO (ohne 19 % USt) berechnet und ausgewiesen.</b> Grundlage: Firmenkunde im EU-Ausland mit erfolgreich geprüfter USt-IdNr.</div>
                        <div><b>Benötigte Unterlagen:</b> geprüfte USt-IdNr. (Prüfprotokoll), Gelangensbestätigung (§17a UStDV), Handelsregisterauszug / Gewerbenachweis des Käufers, Ausweiskopie des Abholers, ggf. Abholvollmacht.</div>
                        <div><b>Ablauf:</b> USt-IdNr. prüfen → Kaufvertrag &amp; Rechnung netto → Zahlung per Überweisung/EC → Fahrzeugübergabe → unterschriebene Gelangensbestätigung nach Ankunft im Bestimmungsland einholen und ablegen.</div>
                        <div className="font-bold text-red-700">⚠️ Keine Finanzierung/Leasing möglich — nur Überweisung oder EC-Karte.</div>
                    </div>
                )}
                {/* Preisübersicht / Zwischensumme */}
                <div className="bg-gray-50 border rounded-sm p-3 text-sm" data-testid="dispo-payment-summary">
                    <div className="flex justify-between"><span>Fahrzeugpreis{euTaxFree ? " (netto)" : ""}</span><span className="font-mono-num">{formatPrice(data?.vehicle_price || 0)}</span></div>
                    <div className="flex justify-between"><span>+ Zubehör{euTaxFree ? " (netto)" : ""}</span><span className="font-mono-num" data-testid="dispo-payment-accessories">{formatPrice(data?.accessories_total || 0)}</span></div>
                    <div className="flex justify-between"><span>+ Dienstleistungen{euTaxFree ? " (netto)" : ""}</span><span className="font-mono-num" data-testid="dispo-payment-services">{formatPrice(data?.services_total || 0)}</span></div>
                    {Number(data?.freetext_total || 0) !== 0 && (
                        <div className="flex justify-between"><span>+ Sonstige Posten</span><span className="font-mono-num" data-testid="dispo-payment-freetext">{formatPrice(data?.freetext_total || 0)}</span></div>
                    )}
                    {Number(data?.boni_total || 0) !== 0 && (
                        <div className="flex justify-between text-emerald-700"><span>− Boni / Aktion</span><span className="font-mono-num" data-testid="dispo-payment-boni">−{formatPrice(Math.abs(data?.boni_total || 0))}</span></div>
                    )}
                    <div className="flex justify-between font-bold border-t mt-1 pt-1"><span>Zwischensumme ({euTaxFree ? "netto, steuerfrei" : "brutto"})</span><span className="font-mono-num" data-testid="dispo-payment-subtotal">{formatPrice(subtotal)}</span></div>
                </div>

                {/* Endpreis + Rabatt */}
                <div className="grid grid-cols-2 gap-3 items-end mt-1">
                    <div>
                        <Label className="text-xs font-bold">Verhandelter Endpreis ({euTaxFree ? "netto" : "brutto"})</Label>
                        <Input type="number" min="0" step="0.01" value={finalPrice} onChange={(e) => { setTouched(true); setFinalPrice(e.target.value); }} data-testid="dispo-payment-final" />
                    </div>
                    <div className="text-right">
                        <div className="text-xs text-gray-500">Rabatt</div>
                        <div className={`text-lg font-black ${discountAmount > 0 ? "text-red-600" : discountAmount < 0 ? "text-emerald-600" : "text-gray-600"}`} data-testid="dispo-payment-discount">
                            {discountAmount >= 0 ? "−" : "+"}{formatPrice(Math.abs(discountAmount))} ({discountPct}%)
                        </div>
                    </div>
                </div>

                {/* Jul-2026 (User-Wunsch · 341) — Rabatt-Limit-Hinweis + Freigabe-Anfrage */}
                {discountLimit?.enabled && (
                    <div className={`text-[11px] rounded-sm border px-2.5 py-1.5 mt-1 ${limitExceeded && !approvalOk ? "bg-red-50 border-red-300 text-red-800 font-bold" : approvalOk && limitExceeded ? "bg-emerald-50 border-emerald-300 text-emerald-800" : "bg-gray-50 border-gray-200 text-gray-600"}`} data-testid="dispo-payment-discount-limit">
                        {discountLimit.no_discount
                            ? <><b className="text-red-700">Kein Rabatt erlaubt</b> (0 %)</>
                            : <>Max. Rabatt: <b className="font-mono-num">{discountLimit.max_pct} %</b></>}
                        {discountLimit.rule_name ? <> (Regel „{discountLimit.rule_name}")</> : null}
                        {Number(discountLimit.stock_days) > 0 && <span className="text-gray-400"> · Standzeit {discountLimit.stock_days} Tage</span>}
                        {limitExceeded && approvalOk && <span className="font-bold"> — Rabatt freigegeben ✓ ({discountApproval.requested_pct} %)</span>}
                        {limitExceeded && !approvalOk && (discountLimit.enforced
                            ? " — Limit überschritten!"
                            : " — Limit überschritten (Leitungs-Override erlaubt).")}
                        {limitExceeded && !approvalOk && discountLimit.enforced && (
                            discountApproval?.status === "pending" && Math.abs((Number(discountApproval.requested_pct) || 0) - discountPct) < 0.02 ? (
                                <div className="mt-1 font-normal text-amber-800" data-testid="dispo-discount-approval-pending">
                                    Freigabe angefordert ({discountApproval.requested_pct} %) — wartet auf Filialleiter/Verkaufsleiter.
                                </div>
                            ) : (
                                <button type="button" onClick={requestDiscountApproval} disabled={reqApprovalBusy}
                                    className="block mt-1.5 px-2.5 py-1 rounded-sm bg-[#111827] text-[#FFCC00] text-[11px] font-bold hover:bg-black disabled:opacity-60"
                                    data-testid="dispo-discount-approval-request">
                                    {reqApprovalBusy ? "Wird angefordert …" : `Freigabe beim Filialleiter anfordern (${discountPct} %)`}
                                </button>
                            )
                        )}
                    </div>
                )}

                {/* Jun-2026 (User-Wunsch) — Inzahlungnahme & Restbetrag (2. Zwischensumme) */}
                <div className="bg-gray-50 border rounded-sm p-3 text-sm mt-1" data-testid="dispo-payment-tradein-summary">
                    <div className="flex justify-between">
                        <span>Inzahlungnahme {tradeInAmount > 0 ? "(verhandelter Eintauschpreis)" : ""}</span>
                        <span className={`font-mono-num ${tradeInAmount > 0 ? "text-emerald-700" : "text-gray-400"}`} data-testid="dispo-payment-inzahlung">
                            {tradeInAmount > 0 ? `−${formatPrice(tradeInAmount)}` : "keine"}
                        </span>
                    </div>
                    {tradeInAmount > 0 && (
                        <>
                            <div className="flex justify-between font-bold border-t mt-1 pt-1">
                                <span>Zu zahlender Restbetrag (brutto)</span>
                                <span className="font-mono-num" data-testid="dispo-payment-net">{formatPrice(netToPay)}</span>
                            </div>
                            <p className="text-[10px] text-gray-400 mt-0.5">Die Zahlungsarten beziehen sich auf den Restbetrag.</p>
                        </>
                    )}
                </div>

                {/* Jul-2026 (User-Wunsch) — Gutschein-Code direkt im Kaufvertrag einlösen */}
                <div className="bg-gray-50 border rounded-sm p-3 text-sm mt-1" data-testid="dispo-payment-voucher">
                    {voucher ? (
                        <>
                            <div className="flex items-center justify-between gap-2">
                                <span className="flex items-center gap-1.5 min-w-0">
                                    <span className="text-[9px] font-bold uppercase tracking-wider bg-emerald-100 text-emerald-800 rounded-sm px-1 py-0.5 shrink-0">Gutschein</span>
                                    <span className="font-mono-num font-bold truncate" data-testid="dispo-payment-voucher-code">{voucher.code}</span>
                                    <button type="button" onClick={removeVoucher} disabled={voucherBusy} className="text-red-500 hover:text-red-700 text-xs underline shrink-0" data-testid="dispo-payment-voucher-remove">entfernen</button>
                                </span>
                                <span className="font-mono-num text-emerald-700 font-bold shrink-0" data-testid="dispo-payment-voucher-amount">−{formatPrice(voucherDiscount)}</span>
                            </div>
                            <div className="flex justify-between font-bold border-t mt-1 pt-1">
                                <span>Zu zahlender Restbetrag (brutto)</span>
                                <span className="font-mono-num" data-testid="dispo-payment-net-voucher">{formatPrice(netToPay)}</span>
                            </div>
                            <p className="text-[10px] text-gray-400 mt-0.5">Der Gutschein wird automatisch verrechnet und im Kaufvertrag ausgewiesen.</p>
                        </>
                    ) : (
                        <div className="flex items-center gap-1.5">
                            <span className="text-xs text-gray-500 shrink-0">Gutschein-Code:</span>
                            <Input value={voucherCode} onChange={(e) => setVoucherCode(e.target.value)}
                                onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); redeemVoucher(); } }}
                                placeholder="z. B. TW-ABCD-1234" className="h-8 text-xs font-mono-num" data-testid="dispo-payment-voucher-input" />
                            <Button type="button" size="sm" variant="outline" className="h-8 text-xs shrink-0" onClick={redeemVoucher} disabled={voucherBusy} data-testid="dispo-payment-voucher-redeem">
                                {voucherBusy ? "…" : "Einlösen"}
                            </Button>
                        </div>
                    )}
                </div>

                {/* Mischzahlung — Zahlungsbedingung wird automatisch aus dem
                    dominanten Zahlungsweg abgeleitet (kein separates Feld). */}
                <div className="mt-2" data-testid="dispo-payment-methods">
                    <div className="flex items-center justify-between mb-1">
                        <Label className="text-xs font-bold uppercase tracking-wider">Zahlungsarten (Mischzahlung)</Label>
                        <Button type="button" size="sm" variant="outline" className="h-7 text-xs" onClick={addPay} data-testid="dispo-payment-add">+ Zahlungsart</Button>
                    </div>
                    {payments.length === 0 && <div className="text-[11px] text-gray-400 py-1">Keine Zahlungsart erfasst (z. B. Bar + Inzahlungnahme + Finanzierung).</div>}
                    {payments.map((p, idx) => {
                        // Inzahlungnahme-Zeile ist bei vorhandenem Eintausch gesperrt
                        // (Betrag = Ankaufspreis, automatisch aus dem Eintausch).
                        const lockedTradein = p.method === "inzahlungnahme" && Number(tradein?.price || 0) > 0;
                        return (
                        <div key={idx} data-testid="dispo-payment-row">
                            <div className="flex items-center gap-2 py-1">
                                <select value={p.method} onChange={(e) => updPay(idx, "method", e.target.value)} disabled={lockedTradein} className="flex-1 border border-gray-300 rounded-sm px-2 py-1.5 text-sm bg-white disabled:bg-gray-100 disabled:text-gray-500" data-testid="dispo-payment-method">
                                    {PAY_METHODS.filter((m) => (!m.needsTradein || Boolean(tradein) || p.method === m.value))
                                        .filter((m) => !(euTaxFree && (m.value === "finanzierung" || m.value === "leasing") && p.method !== m.value)).map((m) => (
                                        <option key={m.value} value={m.value} disabled={Boolean(m.disabled) && p.method !== m.value}>{m.label}</option>
                                    ))}
                                </select>
                                <Input type="number" min="0" step="0.01" value={p.amount} onChange={(e) => updPay(idx, "amount", e.target.value)} readOnly={lockedTradein} title={lockedTradein ? "Automatisch aus dem Eintausch (Ankaufspreis)" : undefined} className={`w-28 h-9 text-sm ${lockedTradein ? "bg-gray-100 text-gray-600" : ""}`} data-testid="dispo-payment-amount" />
                                {lockedTradein ? (
                                    <span className="text-emerald-600 shrink-0" title="Aus Eintausch — wird automatisch befüllt"><Repeat className="w-4 h-4" /></span>
                                ) : (
                                    <button type="button" onClick={() => rmPay(idx)} className="text-red-500 hover:text-red-700" data-testid="dispo-payment-remove"><XCircle className="w-4 h-4" /></button>
                                )}
                            </div>
                            {terminalReady && p.method === "ec_karte" && Number(p.amount) > 0 && (
                                <div className="flex items-center justify-end gap-2 pb-1">
                                    <span className="text-[10px] text-gray-500 uppercase tracking-wider font-bold">SumUp EC-Terminal</span>
                                    <Button type="button" size="sm" variant="outline"
                                        className="h-7 rounded-sm text-[11px] font-bold bg-emerald-600 hover:bg-emerald-500 text-white hover:text-white border-transparent"
                                        onClick={() => sendToTerminal(idx)} disabled={sendingTerminal === idx}
                                        data-testid={`dispo-payment-sumup-send-${idx}`}>
                                        {sendingTerminal === idx ? <Loader2 className="w-3.5 h-3.5 mr-1 animate-spin" /> : <CreditCard className="w-3.5 h-3.5 mr-1" />}
                                        {formatPrice(Number(p.amount) || 0)} ans Terminal senden
                                    </Button>
                                </div>
                            )}
                        </div>
                        );
                    })}
                    {payments.some((p) => p.method === "inzahlungnahme" && Number(tradein?.price || 0) > 0) && (
                        <div className="text-[10px] text-emerald-700 mt-0.5 flex items-center gap-1"><Repeat className="w-3 h-3" /> Inzahlungnahme automatisch aus dem Eintausch befüllt (Ankaufspreis) — keine doppelte Pflege nötig.</div>
                    )}
                    <div className="flex justify-between text-xs mt-1 border-t pt-1">
                        <span>Summe Zahlungen</span>
                        <span className="font-mono-num font-bold">{formatPrice(paidTotal)}</span>
                    </div>
                    <div className={`flex justify-between text-xs font-bold ${Math.abs(balance) > 0.01 ? "text-red-600" : "text-emerald-600"}`} data-testid="dispo-payment-balance">
                        <span>{balance > 0.01 ? "Noch offen" : balance < -0.01 ? "Überzahlt" : "Ausgeglichen"}</span>
                        <span className="font-mono-num">{formatPrice(balance)}</span>
                    </div>
                    {Math.abs(balance) > 0.01 && (
                        <button type="button" onClick={balancePayments} className="mt-1 w-full text-[11px] font-bold text-orange-700 bg-orange-50 border border-orange-300 rounded-sm py-1.5 hover:bg-orange-100 flex items-center justify-center gap-1.5" data-testid="dispo-payment-balance-btn">
                            <Calculator className="w-3.5 h-3.5" /> Restbetrag ausgleichen ({formatPrice(netToPay)})
                        </button>
                    )}
                </div>

                {/* Jun-2026 (User-Wunsch) — Notizen unter die linke Spalte verschoben. */}
                <div className="mt-1" data-testid="dispo-payment-notes-block">
                    <div className="flex items-center justify-between mb-1">
                        <Label className="text-xs font-bold uppercase tracking-wider text-gray-600">Interne Vermerke</Label>
                        <button
                            type="button"
                            onClick={improveNotes}
                            disabled={aiNote}
                            className="inline-flex items-center gap-1 text-[10px] font-bold uppercase tracking-wider px-2 py-1 rounded-sm bg-violet-600 hover:bg-violet-700 text-white disabled:opacity-50"
                            data-testid="dispo-payment-notes-ai"
                            title="Notiztext mit KI verbessern"
                        >
                            {aiNote ? <Loader2 className="w-3 h-3 animate-spin" /> : <Sparkles className="w-3 h-3" />}
                            KI: Text verbessern
                        </button>
                    </div>
                    {isOrderVehicle && (
                        <p className="text-[10px] text-amber-700 mb-1" data-testid="dispo-payment-order-hint">
                            Bestellfahrzeug erkannt — ein Hinweis zu unverbindlichen Lieferterminen wurde vorbelegt.
                        </p>
                    )}
                    <Textarea placeholder="Notizen zu den Konditionen (optional)" value={notes} onChange={(e) => { setTouched(true); setNotes(e.target.value); }} className="text-sm" rows={4} data-testid="dispo-payment-notes" />
                </div>
                  </div>
                  <div className="space-y-2 min-w-0">

                {/* Jun-2026 (User-Wunsch) — Finanzierung: Bank & Konditionen erfassen,
                    sobald „Finanzierung" als Zahlungsart gewählt ist. */}
                {hasFinancing && (
                    <div className="bg-gray-50 border border-gray-200 rounded-sm p-3 mt-2 space-y-2" data-testid="dispo-payment-financing">
                        <Label className="text-xs font-bold uppercase tracking-wider text-gray-700 flex items-center gap-1.5"><Landmark className="w-3.5 h-3.5 text-gray-500" /> {finKind === "leasing" ? "Leasing — Leasinggesellschaft & Konditionen" : "Finanzierung — Bank & Konditionen"}<InfoHint id="dispo-financing" side="right" defaultText="Wird in „Buchhaltung → Ausstehende Bankgelder“ weitergeführt. Die je Zahlungsart benötigten Upload-Belege erscheinen unter „Upload Belege“." /></Label>
                        <div className="grid grid-cols-2 sm:grid-cols-3 gap-x-3 gap-y-2">
                            <div className="sm:col-span-3">
                                <label className="text-[10px] uppercase tracking-wider text-gray-500 font-bold">{finKind === "leasing" ? "Leasinggesellschaft" : "Bank"}</label>
                                {banksFor.length > 0 ? (
                                    <select value={fin.bank_id} onChange={(e) => onBankChange(e.target.value)} className="w-full h-9 text-sm rounded-sm border border-gray-300 px-1.5 bg-white" data-testid="dispo-payment-fin-bank">
                                        <option value="">— wählen —</option>
                                        {banksFor.map((b) => <option key={b.id} value={b.id}>{b.name}{b.city ? ` (${b.city})` : ""}</option>)}
                                    </select>
                                ) : (
                                    <div className="space-y-1">
                                        <select
                                            value={bankCustom ? "__custom__" : fin.bank_name}
                                            onChange={(e) => {
                                                const v = e.target.value;
                                                if (v === "__custom__") { setBankCustom(true); setFinField("bank_name", ""); }
                                                else { setBankCustom(false); setFinField("bank_name", v); }
                                            }}
                                            className="w-full h-9 text-sm rounded-sm border border-gray-300 px-1.5 bg-white"
                                            data-testid="dispo-payment-fin-bank-suggest"
                                        >
                                            <option value="">— wählen —</option>
                                            {(finKind === "leasing" ? DEFAULT_LEASING_NAMES : DEFAULT_BANK_NAMES).map((n) => <option key={n} value={n}>{n}</option>)}
                                            <option value="__custom__">Andere… (manuell)</option>
                                        </select>
                                        {bankCustom && (
                                            <Input value={fin.bank_name} onChange={(e) => setFinField("bank_name", e.target.value)} placeholder="Bankname" className="h-9 text-sm" data-testid="dispo-payment-fin-bankname" />
                                        )}
                                    </div>
                                )}
                            </div>
                            <div><label className="text-[10px] uppercase tracking-wider text-gray-500 font-bold">Antragsnummer</label><Input value={fin.finance_number} onChange={(e) => setFinField("finance_number", e.target.value)} className="h-9 text-sm font-mono-num" data-testid="dispo-payment-fin-number" /></div>
                            <div><label className="text-[10px] uppercase tracking-wider text-gray-500 font-bold">Laufzeit (Monate)</label><Input type="number" min="0" value={fin.term_months} onChange={(e) => setFinField("term_months", e.target.value)} className="h-9 text-sm font-mono-num" data-testid="dispo-payment-fin-term" /></div>
                            <div><label className="text-[10px] uppercase tracking-wider text-gray-500 font-bold">Mtl. Rate (€)</label><Input type="number" min="0" step="0.01" value={fin.monthly_rate} onChange={(e) => setFinField("monthly_rate", e.target.value)} className="h-9 text-sm font-mono-num" data-testid="dispo-payment-fin-monthly" /></div>
                            <div><label className="text-[10px] uppercase tracking-wider text-gray-500 font-bold">Anzahlung (€)</label><Input type="number" min="0" step="0.01" value={fin.down_payment} onChange={(e) => setFinField("down_payment", e.target.value)} className="h-9 text-sm font-mono-num" data-testid="dispo-payment-fin-down" /></div>
                            <div><label className="text-[10px] uppercase tracking-wider text-gray-500 font-bold">Schlussrate (€)</label><Input type="number" min="0" step="0.01" value={fin.final_rate} onChange={(e) => setFinField("final_rate", e.target.value)} className="h-9 text-sm font-mono-num" data-testid="dispo-payment-fin-final" /></div>
                        </div>
                        <label className="flex items-center gap-2 mt-1 cursor-pointer select-none bg-white border border-gray-200 rounded-sm px-2.5 py-2" data-testid="dispo-fin-approved-label">
                            <input type="checkbox" checked={Boolean(fin.financing_approved)} onChange={(e) => setFinField("financing_approved", e.target.checked)}
                                className="w-4 h-4 accent-emerald-600" data-testid="dispo-fin-approved" />
                            <span className="text-xs font-bold text-gray-800">{finKind === "leasing" ? "Leasing durch Leasinggesellschaft genehmigt" : "Finanzierung durch Bank genehmigt"}</span>
                            <InfoHint id="dispo-financing-approved" side="top" defaultText="Solange nicht genehmigt, erscheint eine Erinnerung in „Was zu tun ist“ bei Verkäufer & Filialleiter (alle 3 Tage), bis sie erledigt ist." />
                            {!fin.financing_approved && <span className="ml-auto text-[9px] font-black uppercase bg-amber-100 text-amber-800 px-1.5 py-0.5 rounded-sm">offen</span>}
                        </label>
                    </div>
                )}

                {/* Jun-2026 (User-Wunsch) — Zusatzertrag (Zinsen/Versicherung): NUR bei
                    Finanzierung/Leasing sichtbar; Basis für die Provision auf
                    Zusatzerträge (Einstellungen → Provisionen). */}
                {hasFinancing && (
                <div className="bg-white border-2 border-orange-300 rounded-sm p-3 mt-2 space-y-1.5" data-testid="dispo-payment-extra-earnings">
                    <label className="text-xs font-bold uppercase tracking-wider text-[#111827] flex items-center gap-1.5"><Sparkles className="w-3.5 h-3.5" /> Zusatzertrag (Zinsen/Versicherung) €<InfoHint id="dispo-extra-earnings" side="right" defaultText="Zusätzlich an Zinsen/Versicherungen erwirtschafteter Betrag. Die Verkäufer-Provision hierauf wird gemäß „Einstellungen → Provisionen“ berechnet." /></label>
                    <Input type="number" min="0" step="0.01" value={extraEarnings}
                        onChange={(e) => { setTouched(true); setExtraEarnings(e.target.value); }}
                        placeholder="0,00" className="h-9 text-sm font-mono-num bg-white max-w-[200px]" data-testid="dispo-extra-earnings-input" />
                </div>
                )}

                {/* Phase 5 — Inzahlungnahme erkannt → Eintausch-Fahrzeug erfassen. */}
                {showTradeinHint && (
                    <div className="bg-white border-2 border-orange-300 rounded-sm p-3 flex items-center gap-2 flex-wrap" data-testid="dispo-payment-tradein-hint">
                        <Repeat className="w-4 h-4 text-orange-700" />
                        <span className="text-xs font-bold text-[#111827]">Inzahlungnahme erkannt</span>
                        <span className="text-xs text-gray-600">— erfassen Sie das Eintausch-Fahrzeug & den Ankaufsvertrag.</span>
                        <Button type="button" size="sm" onClick={async () => { const ok = await save(); if (ok) onOpenTradein?.(); }} className="ml-auto bg-orange-500 hover:bg-orange-600 text-white h-8 text-xs" data-testid="dispo-payment-open-tradein">
                            Eintausch-Fahrzeug erfassen →
                        </Button>
                    </div>
                )}
                  </div>
                </div>

                <DialogFooter className="gap-2">
                    <DispoBackButton onBack={onBack ? () => { save().catch(() => {}); onBack(); } : undefined} />
                    <Button variant="outline" onClick={onClose}>Abbrechen</Button>
                    <Button onClick={async () => { const ok = await save(); if (ok) onContinue?.(); }} disabled={saving} className="bg-orange-500 hover:bg-orange-600 text-white" data-testid="dispo-payment-submit">
                        {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : "Weiter zum Kaufvertrag"}
                    </Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
}